import { Field, Formik } from 'formik';
import { Platform, StyleSheet, View } from 'react-native';

import { Button, InlineAlert, NumberMaskInput } from '@fhs/ui';
import { giftCardNumberMask, giftCardPinMask } from '@fhs/ui/src/components/form-text-inputs/masks';

import { getGiftCardSecurityFieldType } from '../../utils/constants';

import { GiftCardFormInputErrors, GiftCardFormInputProps, StepAddCardFormProps } from './types';

const validateForm = (values: GiftCardFormInputProps) => {
  const errors: GiftCardFormInputErrors = {};
  if (!values.cardId || values.cardId.length < 16) {
    errors.cardId = 'Please enter a valid Card ID.';
  }

  const securityFieldType = getGiftCardSecurityFieldType(values.cardId);

  // Only validate PIN/CVV2 when the card number matches a known BIN prefix
  if (securityFieldType !== 'none' && (!values.pin || values.pin.length < 4)) {
    errors.pin =
      securityFieldType === 'cvv2' ? 'Please enter a valid CVV2.' : 'Please enter a valid Pin.';
  }

  return errors;
};

export const StepAddCardForm = ({
  cardId,
  onCardIdChange,
  pin,
  onPinChange,
  isLoadingGiftCard = false,
  onContinue,
  errorMessage,
}: StepAddCardFormProps) => {
  return (
    <Formik validate={validateForm} initialValues={{ cardId, pin }} onSubmit={onContinue}>
      {({ isSubmitting, submitForm, values }) => {
        const securityFieldType = getGiftCardSecurityFieldType(values.cardId);
        const showSecurityField = securityFieldType !== 'none';
        const isCvv2 = securityFieldType === 'cvv2';

        const getInfoMessage = () => {
          if (!showSecurityField) {
            return 'You can find your Card ID on the back of your physical gift card.';
          }
          return isCvv2
            ? 'You can find your Card ID and the CVV2 on the back of your physical gift card.'
            : 'You can find your Card ID and the PIN on the back of your physical gift card.';
        };

        return (
          <>
            <View style={styles.inputContainer}>
              <Field name="cardId">
                {({ field, form, meta }) => {
                  return (
                    <NumberMaskInput
                      value={field.value ?? cardId}
                      onChange={newId => {
                        form.setFieldValue('cardId', newId);
                        form.setSubmitting(false);
                        onCardIdChange(newId);

                        // Reset PIN/CVV2 when security field gets hidden
                        const newSecurityFieldType = getGiftCardSecurityFieldType(newId);
                        if (newSecurityFieldType === 'none') {
                          form.setFieldValue('pin', '');
                          onPinChange('');
                        }
                      }}
                      onBlur={form.handleBlur('cardId')}
                      error={meta.touched && meta.error}
                      style={styles.input}
                      label="Card ID"
                      placeholder="Enter the Card ID"
                      mask={giftCardNumberMask}
                    />
                  );
                }}
              </Field>
            </View>
            {showSecurityField && (
              <View style={styles.inputContainer}>
                <Field name="pin">
                  {({ field, form, meta }) => {
                    return (
                      <NumberMaskInput
                        value={field.value ?? pin}
                        onChange={newPin => {
                          form.setFieldValue('pin', newPin);
                          form.setSubmitting(false);
                          onPinChange(newPin);
                        }}
                        onBlur={form.handleBlur('pin')}
                        error={meta.touched && meta.error}
                        style={styles.input}
                        label={isCvv2 ? 'CVV2' : 'PIN'}
                        placeholder={isCvv2 ? 'Enter the 4-digits CVV2' : 'Enter the 4-digits PIN'}
                        mask={giftCardPinMask}
                      />
                    );
                  }}
                </Field>
              </View>
            )}
            <InlineAlert type="info" message={getInfoMessage()} />
            {errorMessage && (
              <View style={styles.errorAlert}>
                <InlineAlert type="error" message={errorMessage} />
              </View>
            )}
            <Button
              style={styles.continueButton}
              size="lg"
              loading={(isLoadingGiftCard || isSubmitting) && !errorMessage}
              disabled={isLoadingGiftCard || isSubmitting || !!errorMessage}
              onPress={submitForm}
            >
              <Button.Text>Continue</Button.Text>
            </Button>
          </>
        );
      }}
    </Formik>
  );
};

const styles = StyleSheet.create({
  input: {
    maxHeight: 48,
    width: '100%',
    ...Platform.select({
      web: {
        flex: 1,
      },
      default: {
        flex: 0,
      },
    }),
  },
  inputContainer: {
    marginBottom: 16,
    gap: 4,
  },
  errorAlert: {
    marginTop: 12,
  },
  continueButton: {
    marginTop: 16,
  },
});
